"use client";

import React from "react";
import ImageComponent from "@/components/ui/image";
import Link from "next/link";
import { sendGAEvent } from "@next/third-parties/google";

import {
  ANNIVERSARY_COLLECTION_ID,
  THANK_YOU_COLLECTION_ID,
} from "@/constants/product";

// Local images referenced from MobileOccasionGifts / MobileCategories
import BirthdayImg from "@/assets/images/v2/birthday_occ.webp";
import HousewarmingImg from "@/assets/images/v2/housewarming_occ.webp";
import ThankYouImg from "@/assets/images/v2/thankyou.jpg";
import AnniversaryImg from "@/assets/images/v2/bakers_hero2.webp";
import WeddingImg from "@/assets/images/v2/wedding_occ.webp";

const occasions = {
  birthday: {
    title: "Birthday",
    subtitle: "Celebrate with Joy",
    image: BirthdayImg,
    slug: "birthday-gifts",
    id: 27,
  },
  housewarming: {
    title: "Housewarming",
    subtitle: "New Beginnings",
    image: HousewarmingImg,
    slug: "housewarming",
    id: 28,
  },
  thankYou: {
    title: "Thank You",
    subtitle: "A Token of Gratitude",
    image: ThankYouImg,
    slug: "thank-you",
    id: THANK_YOU_COLLECTION_ID,
  },
  wedding: {
    title: "Wedding",
    subtitle: "For the Happy Couple",
    image: WeddingImg,
    slug: "wedding-gifts",
    id: 16,
  },
  anniversary: {
    title: "Anniversary",
    subtitle: "Milestones of Love",
    image: AnniversaryImg,
    slug: "anniversary",
    id: ANNIVERSARY_COLLECTION_ID,
  },
};

export const CuratedNarratives = () => {
  const handleCollectionClick = (collectionName: string) => {
    const formattedName = collectionName.toLowerCase().replace(/ /g, "_");
    sendGAEvent("event", `show_more_${formattedName}_clicked`);
  };

  return (
    <section className="hidden md:block py-24 px-8 max-w-[1440px] mx-auto">
      <header className="text-center max-w-3xl mb-12 md:mb-16 mx-auto">
        <span className="font-outfit uppercase tracking-[0.3em] text-[#735C00] mb-3 text-[10px] font-bold block">
          Personal Curation
        </span>
        <h2 className="text-center font-lora text-[#005F52] mb-2 md:mb-4 text-4xl lg:text-5xl font-semibold leading-[1.25] lg:leading-none">
          <span className="italic font-normal">Special</span> Occasions
        </h2>
        <p className="font-outfit font-normal text-sm text-[#5a5a5a] uppercase tracking-widest mt-2">
          The most meaningful gifts India makes
        </p>
      </header>

      {/* Bento-style Editorial Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-8">
        {/* Large Banner: Birthday (replaces Home & Living) */}
        <Link
          href={`/listing/${occasions.birthday.slug}?id=${occasions.birthday.id}`}
          className="md:col-span-8 group relative overflow-hidden h-[600px] rounded-xl block"
          onClick={() => handleCollectionClick(occasions.birthday.title)}
        >
          <ImageComponent
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            alt={occasions.birthday.title}
            src={occasions.birthday.image}
            fill
            sizes="(max-width: 1024px) 100vw, 66vw"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          <div className="absolute bottom-10 left-10 text-white">
            <span className="font-outfit text-[10px] tracking-widest uppercase text-[#97F4DF] mb-2 block">
              {occasions.birthday.subtitle}
            </span>
            <h3 className="font-lora text-4xl mb-4 font-medium">
              {occasions.birthday.title}
            </h3>
            <span className="text-sm font-outfit font-medium border-b border-white pb-1 group-hover:text-[#97F4DF] group-hover:border-[#97F4DF] transition-colors">
              Shop the Collection
            </span>
          </div>
        </Link>

        {/* Secondary Banners */}
        <div className="md:col-span-4 flex flex-col gap-4">
          {/* Housewarming (replaces Beauty & Wellness) */}
          <Link
            href={`/listing/${occasions.housewarming.slug}?id=${occasions.housewarming.id}`}
            className="flex-1 group relative overflow-hidden rounded-xl block h-[284px]"
            onClick={() => handleCollectionClick(occasions.housewarming.title)}
          >
            <ImageComponent
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              alt={occasions.housewarming.title}
              src={occasions.housewarming.image}
              fill
              sizes="(max-width: 1024px) 50vw, 33vw"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            <div className="absolute bottom-10 left-10 text-white">
              <span className="font-outfit text-[10px] tracking-widest uppercase text-[#97F4DF] mb-2 block">
                {occasions.housewarming.subtitle}
              </span>
              <h3 className="font-lora text-4xl mb-4 font-medium">
                {occasions.housewarming.title}
              </h3>
              <span className="text-sm font-outfit font-medium border-b border-white pb-1 group-hover:text-[#97F4DF] group-hover:border-[#97F4DF] transition-colors">
                Shop the Collection
              </span>
            </div>
          </Link>

          {/* Thank You (replaces Kids) */}
          <Link
            href={`/listing/${occasions.thankYou.slug}?id=${occasions.thankYou.id}`}
            className="flex-1 group relative overflow-hidden rounded-xl block h-[284px]"
            onClick={() => handleCollectionClick(occasions.thankYou.title)}
          >
            <ImageComponent
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              alt={occasions.thankYou.title}
              src={occasions.thankYou.image}
              fill
              sizes="(max-width: 1024px) 50vw, 33vw"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            <div className="absolute bottom-10 left-10 text-white">
              <span className="font-outfit text-[10px] tracking-widest uppercase text-[#97F4DF] mb-2 block">
                {occasions.thankYou.subtitle}
              </span>
              <h3 className="font-lora text-4xl mb-4 font-medium">
                {occasions.thankYou.title}
              </h3>
              <span className="text-sm font-outfit font-medium border-b border-white pb-1 group-hover:text-[#97F4DF] group-hover:border-[#97F4DF] transition-colors">
                Shop the Collection
              </span>
            </div>
          </Link>
        </div>
      </div>

      {/* Smaller Row Banners */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Wedding (replaces Gifts for Friends) */}
        <Link
          href={`/listing/${occasions.wedding.slug}?id=${occasions.wedding.id}`}
          className="group relative overflow-hidden h-72 rounded-xl block"
          onClick={() => handleCollectionClick(occasions.wedding.title)}
        >
          <ImageComponent
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            alt={occasions.wedding.title}
            src={occasions.wedding.image}
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          <div className="absolute bottom-10 left-10 text-white">
            <span className="font-outfit text-[10px] tracking-widest uppercase text-[#97F4DF] mb-2 block">
              {occasions.wedding.subtitle}
            </span>
            <h3 className="font-lora text-4xl mb-4 font-medium">
              {occasions.wedding.title}
            </h3>
            <span className="text-sm font-outfit font-medium border-b border-white pb-1 group-hover:text-[#97F4DF] group-hover:border-[#97F4DF] transition-colors">
              Shop the Collection
            </span>
          </div>
        </Link>

        {/* Anniversary (replaces For Parents) */}
        <Link
          href={`/listing/${occasions.anniversary.slug}?id=${occasions.anniversary.id}`}
          className="group relative overflow-hidden h-72 rounded-xl block"
          onClick={() => handleCollectionClick(occasions.anniversary.title)}
        >
          <ImageComponent
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            alt={occasions.anniversary.title}
            src={occasions.anniversary.image}
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          <div className="absolute bottom-10 left-10 text-white">
            <span className="font-outfit text-[10px] tracking-widest uppercase text-[#97F4DF] mb-2 block">
              {occasions.anniversary.subtitle}
            </span>
            <h3 className="font-lora text-4xl mb-4 font-medium">
              {occasions.anniversary.title}
            </h3>
            <span className="text-sm font-outfit font-medium border-b border-white pb-1 group-hover:text-[#97F4DF] group-hover:border-[#97F4DF] transition-colors">
              Shop the Collection
            </span>
          </div>
        </Link>
      </div>
    </section>
  );
};
